import { wccModules } from 'enums/wccModules';
import Hammer from 'hammerjs';
import { DOMHelpers } from 'helpers/dom';
import { Func } from 'interfaces/func';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import './base.scss';
import { cleanUpGroups, getOrCreateGroup, withProxy } from './helpers';

const allowedEvents = <Array<keyof FancyBoxOptions>>['onInit', 'afterLoad', 'beforeLoad', 'afterClose', 'beforeClose', 'afterShow', 'beforeShow'];
const defaultValidator = () => true;

export interface WCCFancyboxBaseVMConfigRaw {
    group?: SubscribableOrNullableValue<string>
    active?: SubscribableOrNullableValue<boolean>    
}

export interface WCCFancyboxBaseVMConfig {
    data: SubscribableOrNullableValue<FancyBoxGroupItem>
    group?: SubscribableOrNullableValue<string>
    options?: SubscribableOrNullableValue<FancyBoxOptions>
    events?: FancyBoxOptions

    active?: SubscribableOrNullableValue<boolean>

    validate?: Func<boolean>
}

@injectable()
export default class WCCFancyboxBaseVM<T extends WCCFancyboxBaseVMConfigRaw> {
    private options: Subscribable<FancyBoxOptions>
    private events: FancyBoxOptions
    private validate: Func<boolean>

    ready = ko.pureComputed(() => true);
    isActive: Observable<boolean>

    constructor(
        @inject(wccModules.componentConfig) rawConfig: T,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const config = this.buildConfig(rawConfig);

        this.isActive = ko.isWritableObservable(config.active) ? <Observable<boolean>>config.active : ko.observable(ko.unwrap(config.active) ?? false);
        this.options = ko.flattenComputed(<FancyBoxOptions>config.options);
        this.events = config.events ?? {};
        this.validate = config.validate ?? defaultValidator;

        const groupName = ko.flattenComputed(config.group, _.uniqueId('fancybox-'));
        const data = ko.flattenComputed(config.data).extend({ deferred: true });

        const group = groupName.mapSingle(name => getOrCreateGroup(name));

        effects.register(group => {
            group.push(data);

            return [
                () => {
                    ko.utils.arrayRemoveItem(group, data);
                    cleanUpGroups();
                },

                withEffect(isActive => {
                    if (isActive)
                        return this.show(group, data);
                }, [this.isActive])
            ];
        }, [group]);

        effects.register(() => {
            const hammer = new Hammer(<HTMLElement>info.element);
            hammer.on('tap', () => this.onClick());            

            return () => hammer.destroy();
        });

        effects.register([
            DOMHelpers.onNodeEvent(<HTMLElement>info.element, 'wcc-fancybox.click', () => this.onClick())
        ]);

        ko.applyBindingsToNode(info.element, {
            'click': (m: any, e: JQueryEventObject) => {
                e.stopPropagation();
                return true;
            },

            'css': {
                'wcc-fancybox': true
            }
        });
    }

    protected buildConfig(rawConfig: T) {
        return <WCCFancyboxBaseVMConfig>{
            group: rawConfig.group,
            active: rawConfig.active,
            options: this.buildOptions(rawConfig)
        }
    }

    protected buildOptions(rawConfig: T) {
        return <FancyBoxOptions>_(rawConfig).omit('$raw', 'group', 'active');
    }

    private show(group: Array<Subscribable<FancyBoxGroupItem | undefined>>, item: Subscribable<FancyBoxGroupItem | undefined>) {
        const items = ko.pureComputed(() => group.map(item => ko.unwrap(item)));
        const availableItems = items.filter<FancyBoxGroupItem>(item => item != undefined);

        let oFocusedItem: Subscribable<FancyBoxGroupItem | undefined> | undefined = item;

        return withEffect((items, item) => {
            if (items.length > 0) {
                if (oFocusedItem == undefined)
                    oFocusedItem = group[0];

                let idx = items.findIndex(item => item.src == ko.unwrap(oFocusedItem)?.src);
                const isDisposed = ko.observable(false);

                const internalEvents = <FancyBoxOptions>{
                    afterShow: (instance, slide) => {
                        oFocusedItem = group.find(item => ko.unwrap(item)?.src == slide.src);
                    },

                    afterClose: () => {
                        if (!isDisposed()) {
                            isDisposed(true);
                            this.isActive(false);                                                            
                        }
                    }
                }

                const baseOptions = this.options();
                const events = this.events;

                const proxy = withProxy([internalEvents, baseOptions, events], allowedEvents);
                const options = <FancyBoxOptions>_({}).extend(baseOptions, proxy);

                //fancybox writes shit to items so we have to copy them
                const instanceItems = items.map(i => _.clone(i));
                const instanceOptions = _.clone(options);

                const instance = $.fancybox.open(instanceItems, instanceOptions, idx);

                return () => {
                    if (!isDisposed()) {
                        isDisposed(true);
                        (<any>instance).close();
                    }
                }
            }
        }, [availableItems, item]);
    }

    private onClick() {
        if (this.validate()) {
            if (this.isActive())
                this.isActive(false);

            this.isActive(true);
        }
    }
}